<template>
  <!--
    EpisodeInfoContent.vue
    The BODY of an episode's information: description, notes, AI summary,
    transcript, details table, ids. One component shared by
      - the info dialog of the episode context menu (EpisodeContextMenu.vue), and
      - the (i) sheet of BOTH players (EpisodeInfoSheet.vue → MediaPlayer.vue,
        EpisodePlayerDialog.vue).
    Extracted 2026-10-05 (user request: the players' info was "incomplete —
    make them homogeneous"): the players used to show only title, podcast,
    date, duration and description, while the context menu had everything.
    One body means the two can never drift apart again.

    Props:
      episode — the list-row episode (needs at least id; video_id/source for
                the transcript; description/notes are shown straight from it)
      active  — true while the container is open. Opening fetches
                GET /episode/details/:id; closing clears everything, so state
                from one episode never leaks into the next.
  -->
  <div>
        <!-- Description with preserved whitespace and line breaks. Any http(s)
             link inside it is clickable (e.g. a text-to-speech episode's
             "Source: …" line), opening in a new tab like the details table's
             URL row. Built from text segments, never v-html, so a description
             can't inject markup. The segments are kept on ONE line on purpose:
             with pre-wrap, template indentation between them would render as
             visible whitespace. -->
        <div class="text-body-2 mt-4" style="white-space: pre-wrap"><template v-for="(segment, i) in descriptionSegments" :key="i"><a v-if="segment.href" :href="segment.href" target="_blank" rel="noopener noreferrer" class="text-primary" style="word-break: break-all">{{ segment.text }}</a><template v-else>{{ segment.text }}</template></template></div>
        <!-- Notes section: shown only when the episode has personal notes -->
        <template v-if="episode?.notes">
          <v-divider class="mt-4"></v-divider>
          <div class="text-caption text-disabled mt-3 mb-1 d-flex align-center">
            <v-icon icon="mdi-note-text" size="x-small" color="info" class="mr-1"></v-icon>
            Notes
            <span v-if="episode.notes_updated_at" class="ml-2">
              &middot; {{ formatDateTime(episode.notes_updated_at) }}
            </span>
          </div>
          <div class="text-body-2" style="white-space: pre-wrap">
            {{ episode.notes }}
          </div>
        </template>

        <!-- ─── AI Summary (on demand) ─────────────────────────────────────
             Same toggle pattern as Transcript below: nothing is generated
             just because the dialog opened. Generated ON DEMAND from the
             TRANSCRIPT (services/summarize.js), which is itself usually
             already cached — the background job (transcript.js's
             enqueueBackgroundTranscript) proactively fetches the transcript
             for every newly discovered episode, but deliberately stops
             there; summarizing is a paid Groq call, so it only happens when
             a user actually asks for it here. If already generated on a
             previous visit, episodeDetails.summary already has it (bundled
             into the same GET /episode/details/:id this dialog already
             fetches) and expanding just reveals it, no request needed. -->
        <v-divider class="mt-4"></v-divider>
        <div class="mt-3 mb-1">
          <v-btn
            variant="text"
            size="small"
            :prepend-icon="showSummary ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            :loading="summarizing"
            :disabled="!canHaveTranscript"
            @click="toggleSummary"
          >
            {{ showSummary ? 'Hide summary' : 'Show summary' }}
          </v-btn>
          <!-- "Already fetched" indicator — episodeDetails.summary already
               tells us this for free (bundled into the same GET
               /episode/details/:id fetch), so no extra request needed to
               show it BEFORE the section is even expanded. -->
          <v-tooltip v-if="!showSummary && episodeDetails?.summary" text="Already generated — opens instantly" location="top">
            <template #activator="{ props: tipProps }">
              <v-icon v-bind="tipProps" icon="mdi-check-circle" size="x-small" color="teal" class="ml-1" />
            </template>
          </v-tooltip>
          <!-- Transcripts/summaries need a transcript: YouTube captions, or the
               stored text of a text-to-speech episode (see canHaveTranscript).
               Disabling upfront (rather than letting the click through to a
               guaranteed "No captions available") also avoids the several-
               second round trip that answer costs. Not shown until the details
               have loaded, so a text-to-speech episode doesn't flash it. -->
          <div v-if="!canHaveTranscript && episodeDetails" class="text-caption text-disabled mt-1">
            Only available for YouTube and text-to-speech episodes
          </div>
          <!-- Generating a summary from scratch chains TWO server-side steps
               (fetch the transcript if it isn't cached yet, THEN summarize
               it) — 10-20+ seconds is normal for an episode the background
               job hasn't reached, not a stall. Without this, a bare spinner
               on the button looked indistinguishable from broken. -->
          <div v-if="summarizing" class="text-caption text-disabled mt-1">
            Generating summary — fetching the transcript first if it isn't cached yet, this can take up to 20-30 seconds…
          </div>
          <template v-if="showSummary">
            <v-alert
              v-if="summarizeError"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              {{ summarizeError }}
            </v-alert>
            <template v-else-if="episodeDetails?.summary">
              <div v-if="episodeDetails.summary_truncated" class="text-caption text-disabled mt-2 mb-1">
                Based on the first part of this episode
              </div>
              <div class="text-body-2 mt-2" style="white-space: pre-wrap">
                {{ episodeDetails.summary }}
              </div>
            </template>
          </template>
        </div>

        <!-- ─── Transcript (on demand) ─────────────────────────────────────
             Always fetched fresh on first expand rather than bundled into
             episodeDetails — it's much larger than the summary and most
             opens of this dialog won't want it. Usually instant anyway: the
             background job above proactively caches it server-side. -->
        <v-divider class="mt-4"></v-divider>
        <div class="mt-3 mb-1">
          <v-btn
            variant="text"
            size="small"
            :prepend-icon="showTranscript ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            :loading="transcriptLoading"
            :disabled="!canHaveTranscript"
            @click="toggleTranscript"
          >
            {{ showTranscript ? 'Hide transcript' : 'Show transcript' }}
          </v-btn>
          <!-- "Already fetched" indicator — episodeDetails.transcript_cached
               is a cheap filesystem check the server does alongside the rest
               of GET /episode/details/:id (see that route), so this is known
               BEFORE the section is even expanded, with no extra request. -->
          <v-tooltip v-if="!showTranscript && episodeDetails?.transcript_cached" text="Already fetched — opens instantly" location="top">
            <template #activator="{ props: tipProps }">
              <v-icon v-bind="tipProps" icon="mdi-check-circle" size="x-small" color="teal" class="ml-1" />
            </template>
          </v-tooltip>
          <div v-if="!canHaveTranscript && episodeDetails" class="text-caption text-disabled mt-1">
            Only available for YouTube and text-to-speech episodes
          </div>
          <template v-if="showTranscript">
            <v-alert
              v-if="transcriptError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              {{ transcriptError }}
            </v-alert>
            <v-alert
              v-else-if="transcriptResult && !transcriptResult.available"
              type="info"
              variant="tonal"
              density="compact"
              icon="mdi-closed-caption-outline"
              class="mt-2"
            >
              {{ transcriptResult.reason || "No transcript available for this episode." }}
            </v-alert>
            <template v-else-if="transcriptResult?.available">
              <!-- Language override: auto-detection trusts YouTube's own
                   declared language, which is occasionally just WRONG for a
                   given video (not a bug on our end — see
                   retryTranscriptLanguage's comment). Always visible once a
                   transcript is showing, not just when something looks off,
                   since there's no way to detect "wrong" automatically. -->
              <div class="d-flex align-center flex-wrap ga-2 mt-2 mb-1">
                <v-chip v-if="transcriptResult.language" size="small" variant="tonal" color="teal">
                  {{ transcriptResult.language.toUpperCase() }}
                </v-chip>
                <!-- A PROVIDED transcript (a text-to-speech episode's own
                     text) is exactly what was spoken — there are no captions
                     to re-fetch in another language, so no retry for it. -->
                <template v-if="!transcriptResult.provided">
                  <span class="text-caption text-disabled">Wrong language?</span>
                  <v-text-field
                    v-model="languageOverride"
                    density="compact"
                    variant="outlined"
                    hide-details
                    placeholder="it, en, es…"
                    style="max-width: 100px"
                    @keyup.enter="retryTranscriptLanguage"
                  />
                  <v-btn
                    size="small"
                    variant="tonal"
                    :disabled="!languageOverride.trim()"
                    :loading="transcriptLoading"
                    @click="retryTranscriptLanguage"
                  >
                    Retry
                  </v-btn>
                </template>
              </div>
              <div class="transcript-text text-body-2">{{ formattedTranscript }}</div>
            </template>
          </template>
        </div>

        <v-divider class="my-4" />
        <!-- ─── Details table: size, dates, source, storage ─────────────────
             Sits at the BOTTOM, after the description/notes: the description is
             what you open this dialog to read, so the metadata shouldn't push it
             below the fold. Values come from GET /episode/details/:id, fetched
             when the dialog opens (the list payloads carry different column
             subsets per view, and the cached SIZE needs a storage lookup).
             Falls back to the episode already in memory while that's in flight. -->
        <v-table density="compact" class="info-details mb-4">
          <tbody>
            <tr v-for="row in detailRows" :key="row.label">
              <td class="text-disabled py-1" style="width: 40%">
                <v-icon :icon="row.icon" size="x-small" class="mr-1"></v-icon>
                {{ row.label }}
              </td>
              <td class="py-1">
                <!-- A row with an href renders as a link opening in a NEW TAB
                     (the original source page — leaving the app would drop
                     the dialog and the list position). break-all so a long
                     URL wraps inside the cell on a phone instead of forcing
                     the whole table to scroll sideways. -->
                <a
                  v-if="row.href"
                  :href="row.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary"
                  style="word-break: break-all"
                >{{ row.value }}</a>
                <template v-else>{{ row.value }}</template>
                <!-- "~" marks a yt-dlp estimate rather than a measured size -->
                <v-tooltip
                  v-if="row.hint"
                  :text="row.hint"
                  location="top"
                >
                  <template #activator="{ props: tipProps }">
                    <v-icon
                      v-bind="tipProps"
                      icon="mdi-information-outline"
                      size="x-small"
                      class="ml-1 text-disabled"
                    ></v-icon>
                  </template>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Internal IDs for debugging/reference -->
        <div class="text-caption mb-4 text-disabled">
          Episode ID: <b>{{ episode?.id }}</b>
          (videoId: <b>{{ episode?.video_id }}</b>)
        </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { getEpisodeDetails } from '@/api/index.js';
import { getTranscript, summarizeTranscript } from '@/api/transcript.js';
import { formatDateTime, formatBytes, secondsToClockTime } from '@/services/secs_to_human.js';
import { linkifySegments } from '@/utils/linkify.js';
import { voiceLabel } from '@/utils/ttsVoice.js';

// ─── Props ──────────────────────────────────────────────────────────────────
const props = defineProps({
  // The episode to describe (list-row shape)
  episode: { type: Object, default: null },
  // Whether the container (dialog / sheet) is open
  active: { type: Boolean, default: false },
});

// Same name the code had inside EpisodeContextMenu, so the logic below reads
// exactly as it did there.
const episode = computed(() => props.episode || {});

// Transcript/summary generation is yt-dlp captions + Groq, both fundamentally
// YouTube-specific — non-YouTube sources (Rumble, RaiPlaySound, ...) have no
// caption support at all and would just burn the request-latency fixes above
// arriving at "No captions available" every time. `episode.source` is on
// every list row already (statements.js selects it everywhere this menu is
// used), so this is known immediately — no need to wait for episodeDetails.
const isYoutubeEpisode = computed(() => episode.value?.source === 'youtube');

/**
 * The description split into plain-text and link segments, so http(s) URLs
 * in it render as clickable links (the template renders each segment as
 * text or an <a> — never v-html, so a description can't inject markup).
 *
 * Trailing sentence punctuation is left OUT of the link ("…see
 * https://x.it/a." links to https://x.it/a). A closing bracket is only
 * trimmed when the URL has no opening one, so a Wikipedia-style
 * ".../Foo_(bar)" keeps its ")". (The splitting lives in utils/linkify.js,
 * shared with the players' episode info sheet.)
 */
const descriptionSegments = computed(() => linkifySegments(episode.value?.description));

// Transcript + AI Summary are available for YouTube episodes (captions) AND
// for any episode whose transcript is already stored — which is how a
// text-to-speech episode gets them: its own text is saved as the transcript
// when it's created (server: transcript.js saveProvidedTranscript). The
// details fetch reports that as `transcript_cached`, so for non-YouTube
// episodes the buttons enable once the dialog's details have loaded.
const canHaveTranscript = computed(
  () => isYoutubeEpisode.value || !!episodeDetails.value?.transcript_cached,
);

// ─── Info-dialog details ─────────────────────────────────────────────────────

/**
 * Details fetched from GET /episode/details/:id when the info dialog opens.
 * The list endpoints return different column subsets per view (Latest doesn't
 * carry created_at/filesize_approx, etc.) and the cached SIZE needs a storage
 * lookup, so the dialog asks the server rather than trusting the row in memory.
 */
const episodeDetails = ref(null);

// ─── AI Summary section state ────────────────────────────────────────────────
// episodeDetails.summary/summary_truncated (see above) already has a value
// here whenever it was generated on some PREVIOUS visit — bundled into the
// same GET /episode/details/:id fetch, so re-showing it costs nothing. The
// summarizing/summarizeError pair covers the FIRST-time generation request
// (see toggleSummary): summarization is on demand, not proactive — only the
// transcript it's generated FROM is fetched in the background.
const showSummary = ref(false);
const summarizing = ref(false);
const summarizeError = ref(null);

// ─── Transcript section state (on demand, unchanged from TranscriptDialog) ──
const showTranscript = ref(false);
const transcriptLoading = ref(false);
const transcriptError = ref(null);
const transcriptResult = ref(null);
// Manual language-override input (see retryTranscriptLanguage below) — the
// escape hatch for when auto-detection picked the wrong language because
// YouTube's OWN metadata was wrong, not because of a bug in the detection.
const languageOverride = ref('');

// Fetch (or clear) the details whenever the panel becomes visible — or the
// episode changes while it is (the playlist player moving to the next one).
watch(() => [props.active, props.episode?.id], async ([open]) => {
  // Always reset the summary/transcript sections' UI state — a stale expand
  // state or transcript from the PREVIOUS episode must never leak into this
  // one, whether the dialog is closing or opening on a different episode.
  showSummary.value = false;
  summarizeError.value = null;
  showTranscript.value = false;
  transcriptError.value = null;
  transcriptResult.value = null;
  languageOverride.value = '';

  if (!open) {
    episodeDetails.value = null;
    return;
  }
  const id = episode.value?.id;
  if (!id) return;
  try {
    const details = await getEpisodeDetails(id);
    // A slow answer for an episode we've since moved past is dropped
    if (episode.value?.id !== id) return;
    episodeDetails.value = details;
    // A summary already generated on a previous visit costs nothing to show
    // (bundled into the same fetch above, no extra request) — open the
    // section by default instead of making the user click "Show summary"
    // to reveal something that's already sitting right there.
    if (episodeDetails.value?.summary) {
      showSummary.value = true;
    }
  } catch (error) {
    // Non-fatal: the dialog still shows description/notes and whatever the
    // in-memory episode already has (see detailRows' fallbacks).
    console.error('Failed to load episode details:', error);
  }
}, { immediate: true });

/**
 * Show/hide the summary section — generates it ON DEMAND on the FIRST
 * expand only (mirrors toggleTranscript below). The server generates it
 * from whatever transcript is already there — usually already cached by
 * the background job, so this is normally fast despite being a live Groq
 * call. Success is written straight into episodeDetails so a later
 * collapse/re-expand (or a page navigating back to this episode) doesn't
 * re-request it.
 */
const toggleSummary = async () => {
  showSummary.value = !showSummary.value;
  if (!showSummary.value || episodeDetails.value?.summary || summarizing.value) return;

  // If the Transcript section was already expanded in THIS dialog session
  // and came back unavailable, a summary request is guaranteed to fail the
  // exact same way (it generates FROM the transcript) — skip the redundant
  // round trip rather than make the user watch it spin for no reason. This
  // matters more than it might sound: on a genuine "no captions" video the
  // server-side attempt can take LONGER than the Transcript section's own,
  // since /summarize also tries an English fallback before giving up.
  if (transcriptResult.value && !transcriptResult.value.available) {
    summarizeError.value = transcriptResult.value.reason || 'No transcript available to summarize.';
    return;
  }

  const videoId = episode.value?.video_id;
  if (!videoId) {
    summarizeError.value = 'This episode has no source video to summarize.';
    return;
  }
  summarizing.value = true;
  summarizeError.value = null;
  try {
    const data = await summarizeTranscript(videoId);
    if (data.available) {
      episodeDetails.value = {
        ...episodeDetails.value,
        summary: data.summary,
        summary_truncated: data.truncated,
      };
    } else {
      summarizeError.value = data.reason || 'Summarization is not available.';
    }
  } catch (error) {
    console.error('Failed to generate summary:', error);
    summarizeError.value = error.message || 'Failed to generate summary';
  } finally {
    summarizing.value = false;
  }
};

/**
 * Reflow the transcript to one sentence per line, purely for on-screen
 * readability — breaks on . ? ! . A no-op when the server already broke the
 * text into lines itself (some sources' auto-captions have no punctuation at
 * all — see server/src/services/transcript.js's word-count fallback).
 */
const formattedTranscript = computed(() => {
  if (!transcriptResult.value?.text) return '';
  return transcriptResult.value.text.replace(/([.?!])\s+/g, '$1\n');
});

/**
 * Show/hide the transcript section — fetches on the FIRST expand only (the
 * server itself is proactively-cached for new episodes via the same
 * background job as the summary, so this is usually instant regardless).
 */
const toggleTranscript = async () => {
  showTranscript.value = !showTranscript.value;
  if (!showTranscript.value || transcriptResult.value || transcriptLoading.value) return;

  const videoId = episode.value?.video_id;
  if (!videoId) {
    transcriptError.value = 'This episode has no source video to transcribe.';
    return;
  }
  transcriptLoading.value = true;
  try {
    transcriptResult.value = await getTranscript(videoId);
  } catch (error) {
    console.error('Failed to load transcript:', error);
    transcriptError.value = error.message || 'Failed to load transcript';
  } finally {
    transcriptLoading.value = false;
  }
};

/**
 * Manually override the transcript's language and re-fetch — the escape
 * hatch for when YouTube's own metadata language is simply wrong (confirmed
 * on a real episode: a genuinely Italian video whose declared `language`
 * was "es" — see CLAUDE.md's transcript section for the full trace). Always
 * fetches fresh and REPLACES whatever's currently shown, regardless of what
 * was cached. Clears any showing AI summary too — the server already clears
 * episodes.summary when a forced re-fetch replaces the transcript (it would
 * otherwise still reflect the wrong-language text), so the local copy has
 * to follow suit rather than keep showing something now known to be stale.
 */
const retryTranscriptLanguage = async () => {
  const videoId = episode.value?.video_id;
  const lang = languageOverride.value.trim().toLowerCase();
  if (!videoId || !lang || transcriptLoading.value) return;

  transcriptLoading.value = true;
  transcriptError.value = null;
  try {
    transcriptResult.value = await getTranscript(videoId, { language: lang });
    if (episodeDetails.value?.summary) {
      episodeDetails.value = { ...episodeDetails.value, summary: null, summary_truncated: null };
    }
    showSummary.value = false;
    summarizeError.value = null;
  } catch (error) {
    console.error('Failed to refetch transcript in language', lang, error);
    transcriptError.value = error.message || 'Failed to refetch transcript';
  } finally {
    transcriptLoading.value = false;
  }
};

/**
 * The rows rendered in the info dialog's details table. Built from the fetched
 * details, falling back to the in-memory episode while the request is in
 * flight (or if it failed). Rows with no value are omitted entirely, so the
 * table never shows blanks or "null".
 */
const detailRows = computed(() => {
  // Prefer server details; fall back to the list row we already have
  const d = episodeDetails.value || episode.value || {};
  const rows = [];

  // Podcast this episode belongs to (only from the details fetch)
  if (d.podcast_title) {
    rows.push({ label: 'Podcast', icon: 'mdi-podcast', value: d.podcast_title });
  }

  // Duration as HH:MM:SS
  if (d.duration) {
    rows.push({ label: 'Duration', icon: 'mdi-timer-outline', value: secondsToClockTime(d.duration) });
  }

  // File size — exact when measured from storage, "~" when it's yt-dlp's
  // estimate. `size` is only present on the details payload.
  if (d.size) {
    rows.push({
      label: 'Size',
      icon: 'mdi-file-outline',
      value: `${d.size_is_exact ? '' : '~'}${formatBytes(d.size)}`,
      hint: d.size_is_exact
        ? 'Actual size of the cached file'
        : 'Estimated size (from the source metadata) — the episode is not cached yet',
    });
  }

  // Definition of the CACHED video file — e.g. "720p · 1280×720". Only on the
  // details payload (probed with ffprobe server-side the first time the
  // dialog opens, then stored — see GET /episode/details), and only for a
  // cached VIDEO episode; audio and uncached episodes simply omit the row.
  // The "Np" label is the conventional shorthand (height in lines); the exact
  // pixel size follows because letterboxed/odd-aspect uploads (a 1200×720
  // film, say) don't map to the standard 16:9 sizes the label implies.
  if (d.width && d.height) {
    rows.push({
      label: 'Definition',
      icon: 'mdi-high-definition-box',
      value: `${d.height}p · ${d.width}×${d.height}`,
      hint: 'Resolution of the cached file (what was actually downloaded, not the source\'s best available)',
    });
  }

  // When the video was published at the source
  if (d.upload_date) {
    rows.push({ label: 'Uploaded', icon: 'mdi-calendar-arrow-up', value: formatDateTime(d.upload_date) });
  }

  // When the episode was added to FYT
  if (d.created_at) {
    rows.push({ label: 'Added', icon: 'mdi-tray-arrow-down', value: formatDateTime(d.created_at) });
  }

  // Cache state: when it was cached and where it lives, or that it isn't cached
  if (d.cached_at) {
    const where = d.storage === 'local' ? 'local disk' : d.storage === 'r2' ? 'cloud (R2)' : null;
    rows.push({
      label: 'Cached',
      icon: 'mdi-cloud-check-outline',
      value: formatDateTime(d.cached_at) + (where ? ` · ${where}` : ''),
    });
  } else {
    rows.push({ label: 'Cached', icon: 'mdi-cloud-off-outline', value: 'Not cached' });
  }

  // Saved (pinned against cache cleanup) and starred timestamps
  if (d.saved) {
    rows.push({
      label: 'Saved',
      icon: 'mdi-content-save',
      value: d.saved_at ? formatDateTime(d.saved_at) : 'Yes',
    });
  }
  if (d.starred) {
    rows.push({
      label: 'Starred',
      icon: 'mdi-star',
      value: d.starred_at ? formatDateTime(d.starred_at) : 'Yes',
    });
  }

  // Where the episode came from, and whether it's audio or video
  if (d.source && d.source !== 'unknown') {
    rows.push({ label: 'Source', icon: 'mdi-web', value: d.source });
  }

  // The episode's original page at the source, as a link that opens in a
  // NEW TAB (see the template's href handling). original_url is what the
  // episode was added/discovered with; normalized_url and the list row's
  // url are fallbacks. Shown without the scheme to keep the cell short —
  // the href carries the full URL.
  // An UPLOADED episode's original_url is a synthetic upload:// value, so it
  // falls back to source_page_url — the article a text-to-speech episode was
  // read from, or the page a sideloaded file came from.
  const originalUrl = d.original_url || d.normalized_url || d.url;
  const linkUrl = /^https?:\/\//i.test(originalUrl || '') ? originalUrl : d.source_page_url;
  if (linkUrl && /^https?:\/\//i.test(linkUrl)) {
    rows.push({
      label: 'URL',
      icon: 'mdi-open-in-new',
      value: linkUrl.replace(/^https?:\/\//i, ''),
      href: linkUrl,
    });
  }

  // IMDb title page, once "Check IMDb" (More actions) has found one — same
  // new-tab link treatment as the URL row above. Only on the details payload.
  if (d.imdb_url) {
    rows.push({
      label: 'IMDb',
      icon: 'mdi-movie-open-outline',
      value: d.imdb_url.replace(/^https?:\/\/(www\.)?/i, ''),
      href: d.imdb_url,
    });
  }
  if (d.episode_type) {
    rows.push({ label: 'Type', icon: d.episode_type === 'audio' ? 'mdi-music' : 'mdi-video', value: d.episode_type });
  }

  // Apple Podcasts / RSS fields, shown only when actually set. Without these
  // rows the values were invisible everywhere IN THE APP — the only way to
  // confirm a subtitle or episode number had saved was to read the raw feed
  // XML (reported as "can't see the episode subtitle anywhere"). Each is
  // omitted when unset, so this adds nothing for the vast majority of
  // episodes that carry no podcast metadata.
  if (d.itunes_episode != null) {
    rows.push({
      label: 'Episode no.',
      icon: 'mdi-pound',
      // Season only makes sense alongside the episode number, so they share
      // a row rather than adding a mostly-empty second one.
      value: d.itunes_season != null ? `S${d.itunes_season} E${d.itunes_episode}` : String(d.itunes_episode),
    });
  }
  if (d.itunes_episode_type && d.itunes_episode_type !== 'full') {
    // "full" is the default every episode already is — only worth a row when
    // it's something notable (trailer/bonus).
    rows.push({ label: 'Episode type', icon: 'mdi-podcast', value: d.itunes_episode_type });
  }
  if (d.itunes_subtitle) {
    rows.push({ label: 'Subtitle', icon: 'mdi-format-title', value: d.itunes_subtitle });
  }
  if (d.itunes_author) {
    rows.push({ label: 'Author', icon: 'mdi-account-music', value: d.itunes_author });
  }
  if (d.itunes_explicit) {
    rows.push({ label: 'Explicit', icon: 'mdi-alert-octagon-outline', value: 'Yes' });
  }

  // ─── Generation cost, in USD (user request: "even when 0") ─────────────────
  // Text-to-speech rendering: recorded by the server when the speech was
  // generated (episodes.tts_voice / tts_characters / tts_cost_usd). The free
  // Edge voices show $0.00; Grok voices show what the characters cost.
  if (d.tts_voice) {
    const grok = d.tts_voice.startsWith('grok:');
    // Nome leggibile dall'id: utils/ttsVoice.js, condiviso con /fides.
    const parts = [
      formatUsd(d.tts_cost_usd),
      grok ? voiceLabel(d.tts_voice) : `${voiceLabel(d.tts_voice)} (free)`,
    ];
    if (d.tts_characters != null) parts.push(`${Number(d.tts_characters).toLocaleString()} characters`);
    rows.push({ label: 'Text to speech', icon: 'mdi-account-voice', value: parts.join(' · ') });
  } else if (d.transcript_cached) {
    // A stored transcript that didn't come from text-to-speech is YouTube's
    // own captions, fetched with yt-dlp — free. (A text-to-speech episode's
    // transcript is its own text, already covered by the row above.)
    rows.push({
      label: 'Transcription',
      icon: 'mdi-closed-caption-outline',
      value: `${formatUsd(0)} · ${d.source === 'youtube' ? 'YouTube captions' : 'captions'} (free)`,
    });
  }

  return rows;
});

/**
 * formatUsd - A cost in US dollars for the details table: two decimals
 * ("$0.00", "$0.06"), but four for a non-zero amount under a cent so a real
 * cost never reads as free ("$0.0011").
 *
 * @param {number|string|null} amount
 * @returns {string}
 */
function formatUsd(amount) {
  const value = Number(amount) || 0;
  return `$${value > 0 && value < 0.01 ? value.toFixed(4) : value.toFixed(2)}`;
}

// ─── Exposed API ─────────────────────────────────────────────────────────────
/**
 * patchDetails - merge fields into the fetched details (used by the context
 * menu after "Check IMDb" finds a match, so the IMDb row appears at once).
 * Ignored when the details belong to a different episode.
 */
const patchDetails = (episodeId, fields) => {
  if (episodeDetails.value && episodeDetails.value.id === episodeId) {
    episodeDetails.value = { ...episodeDetails.value, ...fields };
  }
};
defineExpose({ patchDetails });
</script>

<style scoped>
/* Long transcripts scroll inside their section instead of stretching the
   dialog/sheet. */
.transcript-text {
  white-space: pre-wrap;
  max-height: 40vh;
  overflow-y: auto;
  line-height: 1.6;
}
</style>
